import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { companiesApi } from "./api";
import { CompanyProfileFields, splitJobTypes } from "./CompanyFormFields";
import { useIndustryOptions } from "../industries/api";
import { Button, Card, PageTitle, StatusBadge } from "../../components/ui";
import { QueryView } from "../../components/states";
import { useAuth } from "../../app/auth-context";
import { ApiError } from "../../lib/api";

interface FormValues {
  name: string;
  area: string;
  industryCode: string;
  jobTypesText: string;
  description: string;
  capacity: number;
  conditions: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  latitude?: number | null;
  longitude?: number | null;
  isPublic: boolean;
}

export function CompanyProfilePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const companyId = user?.companyId ?? "";

  const query = useQuery({
    queryKey: ["companies", companyId],
    queryFn: () => companiesApi.getDetail(companyId),
    enabled: !!companyId,
  });
  const industryQuery = useIndustryOptions();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>();

  useEffect(() => {
    if (query.data) {
      const c = query.data;
      reset({
        name: c.name,
        area: c.area,
        industryCode: c.industryCode,
        jobTypesText: c.jobTypes.join(", "),
        description: c.description,
        capacity: c.capacity,
        conditions: c.conditions,
        contactName: c.contactName,
        contactEmail: c.contactEmail,
        contactPhone: c.contactPhone ?? "",
        latitude: c.latitude,
        longitude: c.longitude,
        isPublic: c.isPublic,
      });
    }
  }, [query.data, reset]);

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      companiesApi.updateProfile(companyId, {
        name: v.name,
        area: v.area,
        industryCode: v.industryCode,
        jobTypes: splitJobTypes(v.jobTypesText),
        description: v.description,
        capacity: Number(v.capacity),
        conditions: v.conditions ?? "",
        contactName: v.contactName,
        contactEmail: v.contactEmail,
        contactPhone: v.contactPhone,
        latitude: v.latitude ?? null,
        longitude: v.longitude ?? null,
        isPublic: v.isPublic,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["companies", companyId] }),
  });

  const apiErr = mutation.error instanceof ApiError ? mutation.error.message : null;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageTitle title="受入情報" subtitle="学校に公開される受入情報を編集できます。" />
      <QueryView query={query}>
        {(c) => (
          <Card>
            <div className="mb-4 flex items-center gap-2">
              <span className="text-sm text-slate-500">審査状況:</span>
              <StatusBadge status={c.status} />
              {c.status === "pending" && (
                <span className="text-xs text-slate-400">承認されると公開されます。</span>
              )}
            </div>
            <form className="space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))}>
              <CompanyProfileFields
                register={register}
                errors={errors}
                industry={{
                  isLoading: industryQuery.isLoading,
                  isError: industryQuery.isError,
                  data: industryQuery.data,
                  refetch: () => void industryQuery.refetch(),
                  currentCode: c.industryCode,
                  currentLabel: c.industryLabel,
                }}
                location={{
                  value: {
                    latitude: watch("latitude") ?? null,
                    longitude: watch("longitude") ?? null,
                  },
                  onChange: (v) => {
                    setValue("latitude", v?.latitude ?? null);
                    setValue("longitude", v?.longitude ?? null);
                  },
                }}
              />
              {c.status === "approved" && (
                <label className="flex items-center gap-2 text-sm font-bold text-slate-600">
                  <input type="checkbox" {...register("isPublic")} className="h-4 w-4" />
                  この受入情報を公開する（学校の検索に表示）
                </label>
              )}
              {apiErr && <p className="text-sm font-bold text-rose-500">{apiErr}</p>}
              {mutation.isSuccess && !isDirty && (
                <p className="text-sm font-bold text-emerald-600">保存しました。</p>
              )}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={mutation.isPending || industryQuery.isLoading || industryQuery.isError}
                >
                  保存する
                </Button>
              </div>
            </form>
          </Card>
        )}
      </QueryView>
    </div>
  );
}
